import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    featureDescriptions: {
        /** Enterprise feature - Account-level permission controls */
        adminControls: {
            title: string;
            description: string;
        };
        /** Pro+ feature - Attach files to comments */
        commentAttachments: {
            title: string;
            description: string;
        };
        /** Enterprise feature - Advanced security policies */
        contentSecurity: {
            title: string;
            description: string;
        };
        /** Pro+ feature - Custom branding for share links */
        customBrandedShares: {
            title: string;
            description: string;
        };
        /** Pro+ feature - Team-only commenting */
        internalComments: {
            title: string;
            description: string;
        };
        /** Pro+ feature - Access-controlled projects and folders */
        restrictedProjects: {
            title: string;
            description: string;
        };
        /** Team feature - Multiple user seats */
        members: {
            title: string;
            description: string;
        };
        /** Enterprise/Team feature - Multiple workspace organization */
        multipleWorkspaces: {
            title: string;
            description: string;
        };
        /** Team feature - Storage that scales with team size */
        projectsAndStorageScaled: {
            title: string;
            description: (formattedPerUserStorage: string) => string;
        };
        /** Pro feature - Fixed storage allocation */
        projectsAndStorageFixed: {
            title: (formattedActiveStorage: string) => string;
            description: string;
        };
        /** Enterprise feature - Organization-level access controls */
        restrictedAccess: {
            title: string;
            description: string;
        };
        /** Pro+ feature - Advanced share link security */
        shareLinkSecurity: {
            title: string;
            description: string;
        };
        /** Enterprise feature - SAML SSO integration */
        singleSignOn: {
            title: string;
            description: string;
        };
        /** Team feature - Scalable storage with team growth */
        storageScaled: {
            title: (formattedActiveStorage: string, formattedPerUserStorage: string) => string;
            description: string;
        };
        /** Pro/Team feature - Fixed storage capacity */
        storageFixed: {
            title: (formattedActiveStorage: string) => string;
            description: string;
        };
        /** Enterprise feature - Session-based watermarking */
        watermark: {
            title: string;
            description: string;
        };
    };
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Feature descriptions shown in plan upgrade modal - displayed when user views upgrade options */
            featureDescriptions: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Enterprise feature - Account-level permission controls */
                        adminControls: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.adminControls.title?c=27gkhvllt5rdq", defaultMessage: "Admin Controls" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.adminControls.description?c=39kl9q7twkkv5", defaultMessage: "Control Share permissions at the account level with default settings and the ability to grant Share access to users from specific email domains." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Pro+ feature - Attach files to comments */
                        commentAttachments: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.commentAttachments.title?c=3saamj7e9jb2b", defaultMessage: "Comment Attachments" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.commentAttachments.description?c=2ufbf5vkn3eox", defaultMessage: "Add image, video, and document attachments to your comments." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Enterprise feature - Advanced security policies */
                        contentSecurity: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.contentSecurity.title?c=1euhtba27rl20", defaultMessage: "Content Security Settings" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.contentSecurity.description?c=2754gwp0398hb", defaultMessage: "Manage Share security, including access types, passphrase, expiration, and more." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Pro+ feature - Custom branding for share links */
                        customBrandedShares: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.customBrandedShares.title?c=2mk60natasbc7", defaultMessage: "Custom-Branded Shares" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.customBrandedShares.description?c=1q44emgtj9o0b", defaultMessage: "Upload a custom background image, logo, and description, and display or hide creator info." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Pro+ feature - Team-only commenting */
                        internalComments: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.internalComments.title?c=3gbqclspvx0ye", defaultMessage: "Internal Comments" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.internalComments.description?c=1lie3bv5x6f6y", defaultMessage: "Separate internal team discussion from external stakeholder comments in Shares." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Pro+ feature - Access-controlled projects and folders */
                        restrictedProjects: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.restrictedProjects.title?c=2wou67nrasfr2", defaultMessage: "Restricted Projects & Folders" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.restrictedProjects.description?c=1r2nddjeduyyg", defaultMessage: "Create projects and folders accessible only to you and the people you invite." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Team feature - Multiple user seats */
                        members: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.members.title?c=3v7bfiu8mze2z", defaultMessage: "Up to 15 Members" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.members.description?c=3kw1cs2mmkr7m", defaultMessage: "Add more users to your account for maximum collaboration on projects." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Enterprise/Team feature - Multiple workspace organization */
                        multipleWorkspaces: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.multipleWorkspaces.title?c=2plpff7wqupzd", defaultMessage: "Multiple Workspaces" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.multipleWorkspaces.description?c=2pybvdq59pnyd", defaultMessage: "Create separate Workspaces for each client, department, production, or brand." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Team feature - Storage that scales with team size */
                        projectsAndStorageScaled: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.projectsAndStorageScaled.title?c=18g723qq3ev9i", defaultMessage: "Unlimited Projects & Scaled Storage" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedPerUserStorage: string): string => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.projectsAndStorageScaled.description?c=yb7h7ka7g0pp", defaultMessage: "Get {formattedPerUserStorage} more storage for each Member added to your account." }, { formattedPerUserStorage: formattedPerUserStorage }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Pro feature - Fixed storage allocation */
                        projectsAndStorageFixed: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedActiveStorage: string): string => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.projectsAndStorageFixed.title?c=34znn1967pmoo", defaultMessage: "Unlimited Projects & {formattedActiveStorage} Storage" }, { formattedActiveStorage: formattedActiveStorage }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.projectsAndStorageFixed.description?c=txbfr8fwnm6g", defaultMessage: "Increase your storage with higher plan limits." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Enterprise feature - Organization-level access controls */
                        restrictedAccess: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.restrictedAccess.title?c=7m646p6oelei", defaultMessage: "Restricted Access" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.restrictedAccess.description?c=1jlbvxd379xum", defaultMessage: "Ensure that your sensitive content can only be accessed by people in your organization and users you''ve added." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Pro+ feature - Advanced share link security */
                        shareLinkSecurity: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.shareLinkSecurity.title?c=2bh4gmg8e65a6", defaultMessage: "Share Link Security & Permissions" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.shareLinkSecurity.description?c=zy93occuegmu", defaultMessage: "Enable passphrase protection, link expiration, and disable downloads and comments." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Enterprise feature - SAML SSO integration */
                        singleSignOn: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.singleSignOn.title?c=3k683u3o6lzp2", defaultMessage: "Single Sign-On" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.singleSignOn.description?c=1qqqq0m3ugvgz", defaultMessage: "Fast, secure login with your existing SAML 2.0 SSO provider such as Okta, Google, or Microsoft." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Team feature - Scalable storage with team growth */
                        storageScaled: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedActiveStorage: string, formattedPerUserStorage: string): string => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.storageScaled.title?c=3lqnpxyn56gwd", defaultMessage: "{formattedActiveStorage} Storage + {formattedPerUserStorage} per additional Member" }, { formattedActiveStorage: formattedActiveStorage, formattedPerUserStorage: formattedPerUserStorage }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.storageScaled.description?c=3mu0e5ryyoan4", defaultMessage: "Scalable storage that grows with your team. Add a Member, get 2 TB of storage\u2014automatically." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Pro/Team feature - Fixed storage capacity */
                        storageFixed: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedActiveStorage: string): string => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.storageFixed.title?c=t61swrqdpbsr", defaultMessage: "{formattedActiveStorage} Storage" }, { formattedActiveStorage: formattedActiveStorage }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.storageFixed.description?c=1u89nfp8jou1u", defaultMessage: "Have even more space to store your media and assets within Frame.io." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Enterprise feature - Session-based watermarking */
                        watermark: {
                            get() {
                                return Object.defineProperties({
                                    [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                                }, {
                                    title: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.watermark.title?c=13y84bfm8yiuk", defaultMessage: "Session-Based Watermarking" }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    },
                                    description: {
                                        get() {
                                            return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanUpgradeFeatures.featureDescriptions.watermark.description?c=12m0elc9bhwbo", defaultMessage: "Embed the viewer''s email, IP, geolocation, and more to identify the original stream source." }));
                                        },
                                        enumerable: true,
                                        configurable: true
                                    }
                                });
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
